import React, {useCallback, useEffect, useState} from 'react'
import {Alert, Badge, Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {URL as BACKEND_URL} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {parseId} from '../../../helpers'

interface Props {
  isOpen: boolean
  onClose: () => void
  stage: {id: string; name: string}
}

interface TagOption {
  id: string
  name: string
  color: string
}

interface WebhookConfig {
  id: number
  token: string
  webhook_secret: string
  webhook_url: string
  is_active: boolean
  type_lead: 'PERSONAL' | 'COMERCIAL'
  lead_source_id: string | null
  lead_source_name: string | null
  campus_id: string | null
  campus_name: string | null
  name_field: string
  email_field: string
  phone_field: string
  tags: TagOption[]
  last_triggered_at: string | null
  leads_count: number
}

interface LeadSourceCategory {
  id: string
  name: string
  sources: {id: string; name: string}[]
}

interface Options {
  categories: LeadSourceCategory[]
  campuses: {id: string; name: string}[]
  tags: TagOption[]
}

type CopyField = 'url' | 'secret' | null

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

export const ModalFacebookLeadsWebhook: React.FC<Props> = ({isOpen, onClose, stage}) => {
  const stageNumericId = Number(parseId(stage.id))

  const [config, setConfig] = useState<WebhookConfig | null>(null)
  const [options, setOptions] = useState<Options | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [copied, setCopied] = useState<CopyField>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)

  // Form state
  const [categoryId, setCategoryId] = useState('')
  const [leadSourceId, setLeadSourceId] = useState('')
  const [campusId, setCampusId] = useState('')
  const [typeLead, setTypeLead] = useState<'PERSONAL' | 'COMERCIAL'>('PERSONAL')
  const [isActive, setIsActive] = useState(true)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  // Field mapping
  const [nameField, setNameField] = useState('full_name')
  const [emailField, setEmailField] = useState('email')
  const [phoneField, setPhoneField] = useState('phone_number')

  const loadConfig = useCallback(async () => {
    if (!stageNumericId) return
    setLoadingConfig(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/facebook-leads/webhook/${stageNumericId}/`, {
        headers: authHeaders(),
      })
      if (res.status === 404) {
        setConfig(null)
      } else if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setLeadSourceId(data.lead_source_id || '')
        setCampusId(data.campus_id || '')
        setTypeLead(data.type_lead)
        setIsActive(data.is_active)
        setSelectedTagIds(data.tags.map((t) => t.id))
        setNameField(data.name_field || 'full_name')
        setEmailField(data.email_field || 'email')
        setPhoneField(data.phone_field || 'phone_number')
      } else {
        toast.error('Error loading webhook config')
      }
    } catch {
      toast.error('Network error loading config')
    } finally {
      setLoadingConfig(false)
    }
  }, [stageNumericId])

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/facebook-leads/webhook/options/`, {
        headers: authHeaders(),
      })
      if (res.ok) {
        const data: Options = await res.json()
        setOptions(data)
      }
    } catch {
      // non-blocking
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setConfig(null)
      setOptions(null)
      setCategoryId('')
      setLeadSourceId('')
      setCampusId('')
      setTypeLead('PERSONAL')
      setIsActive(true)
      setSelectedTagIds([])
      setNameField('full_name')
      setEmailField('email')
      setPhoneField('phone_number')
      setConfirmDelete(false)
      setConfirmRegenerate(false)
      return
    }
    loadConfig()
    loadOptions()
  }, [isOpen, loadConfig, loadOptions])

  // Resolve category from loaded config's lead_source_id
  useEffect(() => {
    if (!config?.lead_source_id || !options) return
    for (const cat of options.categories) {
      if (cat.sources.some((s) => s.id === config.lead_source_id)) {
        setCategoryId(cat.id)
        break
      }
    }
  }, [config, options])

  const filteredSources =
    options?.categories.find((c) => c.id === categoryId)?.sources || []

  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    )
  }

  const handleSave = async () => {
    if (!leadSourceId) {
      toast.warning('Please select a lead source')
      return
    }
    if (!campusId) {
      toast.warning('Please select a campus')
      return
    }
    setSaving(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/facebook-leads/webhook/${stageNumericId}/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          lead_source_id: leadSourceId,
          campus_id: campusId,
          type_lead: typeLead,
          is_active: isActive,
          tag_ids: selectedTagIds,
          name_field: nameField.trim() || 'full_name',
          email_field: emailField.trim() || 'email',
          phone_field: phoneField.trim() || 'phone_number',
        }),
      })
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setSelectedTagIds(data.tags.map((t) => t.id))
        toast.success(config ? 'Webhook updated' : 'Webhook created')
      } else {
        const err = await res.json()
        toast.error(err?.error || 'Error saving webhook')
      }
    } catch {
      toast.error('Network error saving webhook')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/facebook-leads/webhook/${stageNumericId}/delete/`,
        {method: 'DELETE', headers: authHeaders()}
      )
      if (res.ok) {
        setConfig(null)
        setConfirmDelete(false)
        toast.success('Webhook deleted')
      } else {
        toast.error('Error deleting webhook')
      }
    } catch {
      toast.error('Network error deleting webhook')
    } finally {
      setDeleting(false)
    }
  }

  const handleRegenerate = async () => {
    setRegenerating(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/facebook-leads/webhook/${stageNumericId}/regenerate/`,
        {method: 'POST', headers: authHeaders()}
      )
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setConfirmRegenerate(false)
        toast.success('Token and secret regenerated — update your Zapier webhook URL')
      } else {
        toast.error('Error regenerating token')
      }
    } catch {
      toast.error('Network error regenerating token')
    } finally {
      setRegenerating(false)
    }
  }

  const copyToClipboard = async (text: string, field: CopyField) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(field)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error('Could not copy to clipboard')
    }
  }

  const isLoading = loadingConfig || loadingOptions

  return (
    <Modal show={isOpen} onHide={onClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-2'>
          <i className='bi bi-facebook text-primary' style={{fontSize: '1.3rem'}} />
          Facebook Lead Ads Webhook — {stage.name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner animation='border' />
          </div>
        ) : (
          <>
            {config && (
              <>
                <Alert variant='info' className='py-2 mb-3'>
                  <small>
                    Configura Zapier: <strong>Facebook Lead Ads → Webhooks by Zapier (POST)</strong>. Pega la URL y el secret en el Zap.
                  </small>
                </Alert>

                {/* Webhook URL */}
                <Form.Group className='mb-3'>
                  <Form.Label className='fw-bold'>Webhook URL</Form.Label>
                  <div className='input-group'>
                    <Form.Control
                      type='text'
                      readOnly
                      value={config.webhook_url}
                      className='font-monospace'
                      style={{fontSize: '0.8rem'}}
                    />
                    <Button
                      variant={copied === 'url' ? 'success' : 'outline-secondary'}
                      onClick={() => copyToClipboard(config.webhook_url, 'url')}
                    >
                      <i className={`bi ${copied === 'url' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                    </Button>
                  </div>
                </Form.Group>

                {/* Webhook Secret */}
                <Form.Group className='mb-4'>
                  <Form.Label className='fw-bold'>Webhook Secret</Form.Label>
                  <div className='input-group'>
                    <Form.Control
                      type='text'
                      readOnly
                      value={config.webhook_secret}
                      className='font-monospace'
                    />
                    <Button
                      variant={copied === 'secret' ? 'success' : 'outline-secondary'}
                      onClick={() => copyToClipboard(config.webhook_secret, 'secret')}
                    >
                      <i className={`bi ${copied === 'secret' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                    </Button>
                  </div>
                  <Form.Text className='text-muted'>
                    Envía este valor en el campo <code>webhook_secret</code> del payload de Zapier.
                  </Form.Text>
                </Form.Group>

                <hr />

                {/* Stats */}
                <div className='d-flex gap-3 mb-4'>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <div className='fw-bold fs-4'>{config.leads_count}</div>
                    <small className='text-muted'>Leads received</small>
                  </div>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <div className='fw-bold' style={{fontSize: '0.85rem'}}>
                      {config.last_triggered_at
                        ? new Date(config.last_triggered_at).toLocaleString()
                        : '—'}
                    </div>
                    <small className='text-muted'>Last triggered</small>
                  </div>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <Badge bg={config.is_active ? 'success' : 'secondary'} className='fs-6'>
                      {config.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <br />
                    <small className='text-muted'>Status</small>
                  </div>
                </div>

                <hr />
              </>
            )}

            {/* Field mapping */}
            <h6 className='mb-2'>
              <i className='bi bi-diagram-3 me-2' />
              Field mapping
            </h6>
            <p className='text-muted small mb-3'>
              Indica qué clave del JSON que envía Zapier corresponde a cada campo del lead.
            </p>
            <div className='row g-3 mb-4'>
              <div className='col-md-4'>
                <Form.Group>
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='full_name'
                    value={nameField}
                    onChange={(e) => setNameField(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className='col-md-4'>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='email'
                    value={emailField}
                    onChange={(e) => setEmailField(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className='col-md-4'>
                <Form.Group>
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='phone_number'
                    value={phoneField}
                    onChange={(e) => setPhoneField(e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>

            <hr />

            {/* Config form */}
            <h6 className='mb-3'>{config ? 'Update settings' : 'Create webhook for this stage'}</h6>

            {/* Lead Source — 2 levels */}
            <Form.Group className='mb-3'>
              <Form.Label>Lead Source Category</Form.Label>
              <Form.Select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value)
                  setLeadSourceId('')
                }}
                disabled={loadingOptions}
              >
                <option value=''>— Select category —</option>
                {options?.categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Lead Source</Form.Label>
              <Form.Select
                value={leadSourceId}
                onChange={(e) => setLeadSourceId(e.target.value)}
                disabled={!categoryId || loadingOptions}
              >
                <option value=''>— Select source —</option>
                {filteredSources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Campus */}
            <Form.Group className='mb-3'>
              <Form.Label>Campus</Form.Label>
              <Form.Select
                value={campusId}
                onChange={(e) => setCampusId(e.target.value)}
                disabled={loadingOptions}
              >
                <option value=''>— Select campus —</option>
                {options?.campuses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Type lead */}
            <Form.Group className='mb-3'>
              <Form.Label>Lead type</Form.Label>
              <div className='d-flex gap-3'>
                <Form.Check
                  type='radio'
                  label='Personal'
                  id='fb-type-personal'
                  checked={typeLead === 'PERSONAL'}
                  onChange={() => setTypeLead('PERSONAL')}
                />
                <Form.Check
                  type='radio'
                  label='Commercial'
                  id='fb-type-comercial'
                  checked={typeLead === 'COMERCIAL'}
                  onChange={() => setTypeLead('COMERCIAL')}
                />
              </div>
            </Form.Group>

            {/* Tags */}
            {options && options.tags.length > 0 && (
              <Form.Group className='mb-3'>
                <Form.Label>Auto-assign tags</Form.Label>
                <div className='d-flex flex-wrap gap-2 p-2 border rounded'>
                  {options.tags.map((tag) => {
                    const selected = selectedTagIds.includes(tag.id)
                    return (
                      <button
                        key={tag.id}
                        type='button'
                        onClick={() => toggleTag(tag.id)}
                        style={{
                          background: selected ? (tag.color || '#6c757d') : 'transparent',
                          border: `2px solid ${tag.color || '#6c757d'}`,
                          borderRadius: '20px',
                          padding: '3px 12px',
                          fontSize: '0.82rem',
                          fontWeight: selected ? 600 : 400,
                          color: selected ? '#fff' : (tag.color || '#6c757d'),
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {selected && <i className='bi bi-check me-1' />}
                        {tag.name}
                      </button>
                    )
                  })}
                </div>
                {selectedTagIds.length > 0 && (
                  <small className='text-muted mt-1 d-block'>
                    {selectedTagIds.length} tag{selectedTagIds.length > 1 ? 's' : ''} selected — applied automatically to every lead from this webhook
                  </small>
                )}
              </Form.Group>
            )}

            {/* Active toggle (only when editing) */}
            {config && (
              <Form.Group className='mb-3'>
                <Form.Check
                  type='switch'
                  id='fb-webhook-active-toggle'
                  label='Webhook active'
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              </Form.Group>
            )}

            {/* Danger zone */}
            {config && (
              <div className='border border-warning rounded p-3 mt-4'>
                <p className='fw-bold mb-2 text-warning'>
                  <i className='bi bi-exclamation-triangle me-2' />
                  Danger zone
                </p>

                {!confirmRegenerate ? (
                  <Button
                    variant='outline-warning'
                    size='sm'
                    className='me-2'
                    onClick={() => setConfirmRegenerate(true)}
                  >
                    <i className='bi bi-arrow-repeat me-1' />
                    Regenerate token & secret
                  </Button>
                ) : (
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <small className='text-warning'>This invalidates the current URL. Update Zapier.</small>
                    <Button
                      variant='warning'
                      size='sm'
                      onClick={handleRegenerate}
                      disabled={regenerating}
                    >
                      {regenerating ? <Spinner size='sm' animation='border' /> : 'Confirm regenerate'}
                    </Button>
                    <Button variant='link' size='sm' onClick={() => setConfirmRegenerate(false)}>
                      Cancel
                    </Button>
                  </div>
                )}

                {!confirmDelete ? (
                  <Button variant='outline-danger' size='sm' onClick={() => setConfirmDelete(true)}>
                    <i className='bi bi-trash me-1' />
                    Delete webhook
                  </Button>
                ) : (
                  <div className='d-flex align-items-center gap-2'>
                    <small className='text-danger'>Delete this webhook config?</small>
                    <Button variant='danger' size='sm' onClick={handleDelete} disabled={deleting}>
                      {deleting ? <Spinner size='sm' animation='border' /> : 'Yes, delete'}
                    </Button>
                    <Button variant='link' size='sm' onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Close
        </Button>
        {!isLoading && (
          <Button variant='primary' onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size='sm' animation='border' className='me-2' />
                Saving…
              </>
            ) : config ? (
              'Update webhook'
            ) : (
              'Create webhook'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
